import { SupportLayout } from "@/components/support-layout";

export const Privacy = () => {
  return (
    <SupportLayout>
      <div>
          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          
          <div className="text-sm text-muted-foreground mb-8">
            <p>Last Updated: 16 December 2025</p>
            <p>Effective Date: 16 December 2025</p>
          </div>

          <div className="prose prose-invert max-w-none space-y-6 text-sm leading-relaxed">
            <p>
              Nordex / AltariGroup ("Nordex", "we", "us", or "our") values your privacy. This Privacy Policy ("Policy") describes how we collect, use, and disclose information that we obtain about individuals who use our websites, mobile applications, browser extensions, APIs, and related services (collectively, the "Interface" or "Services").
            </p>
            
            <p>
              Your use of the Interface, and any dispute over privacy, is subject to this Policy and our Terms of Use, including applicable limitations on damages and provisions for resolution of disputes.
            </p>

            <div className="bg-card border border-border rounded-lg p-4 my-6">
              <p className="font-semibold mb-2">Overview</p>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Privacy is core to Nordex's mission and values</li>
                <li>Nordex does not require you to provide your name, tax ID, or phone number to use basic functionality</li>
                <li>We will never use your IP address for marketing or advertising purposes other than to comply with jurisdictional legal requirements</li>
                <li>Our systems are not designed to associate your IP address with potentially identifying information such as your wallet address or email address</li>
                <li>We may use country-level location data to ensure marketing communications are not sent to restricted jurisdictions</li>
                <li>This Policy will be updated whenever there are changes to our privacy practices or applicable law</li>
              </ul>
            </div>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">1. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">1.1 Information We Collect Directly From You</h3>
              <p>
                You may browse the Interface without registering with us or submitting personal information, however certain information is collected from you as described below:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Any username or avatar you choose to create in connection with your account, including images or media you upload</li>
                <li>Your email or other contact information (to the extent you provide it) in connection with survey responses, customer support queries, promotion entries, or email newsletter sign-ups</li>
                <li>If you choose to use certain opt-in functionality, our third-party partners (such as Privy) may process Know Your Customer (KYC) information on our behalf, including your legal name, date of birth, government-issued identification, contact information, and biometric data. We do not maintain, store, or retain this information in our own systems</li>
                <li>Feedback, comments, ideas, and suggestions for improvements to the Interface</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">1.2 Information We Collect Automatically</h3>
              <p>When you interact with the Interface, we automatically collect:</p>
              
              <p className="mt-4 font-semibold">Basic Information:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>IP address (processed temporarily and only as necessary for security, approximate location, network connection, or legal compliance)</li>
                <li>Country information</li>
                <li>Device type, model, and operating system</li>
                <li>Language and time zone settings</li>
                <li>Browser (plug-in) type and version</li>
                <li>Device network information, including network access form, wireless router identification, and network operator information</li>
              </ul>

              <p className="mt-4 font-semibold">Usage and Diagnostics Data:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Browser type and operating system</li>
                <li>Hardware model and unique device identifiers</li>
                <li>Access times, pages viewed, links clicked</li>
                <li>Web pages that you connect your wallet to</li>
                <li>Conversion events, user preferences, crash logs</li>
                <li>Device information and data collected via cookies and similar technologies</li>
              </ul>

              <p className="mt-4 font-semibold">Financial and Trading Information:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your blockchain network address (on-chain address)</li>
                <li>Cryptocurrency wallet information</li>
                <li>Transaction history</li>
                <li>Trading data and associated fees paid</li>
                <li>Account balances</li>
                <li>Deposit and withdrawal history</li>
                <li>Orders submitted</li>
                <li>API usage</li>
                <li>Funding payments and liquidation events</li>
              </ul>

              <p className="mt-4 font-semibold">Publicly Available Data:</p>
              <p className="mt-2">
                We may collect publicly available data such as your digital asset wallet address and public blockchain data, which is only used to provide our Interface, improve and develop our Interface, and for other legal reasons as described below.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">1.3 Information from Cookies and Tracking Technologies</h3>
              <p>
                We, and third parties we authorize, may use cookies, web beacons, pixels, local storage, device identifiers, and similar technologies to record your preferences, track use of the Interface, and collect information about your use of the Interface. You may choose to set your web browser to refuse cookies or alert you when cookies are being sent. If you do so, please note that some parts of the Interface may not function properly.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">1.4 Information We Receive from Third Parties</h3>
              <p>
                Sometimes, we receive personal information about you from other sources as required or permitted by applicable law, including public databases. We may combine information collected from these sources with information we get from the Interface to comply with legal obligations and limit use of the Interface in connection with fraudulent or illicit activities.
              </p>
              <p className="mt-4">
                If you create or log into your account through a third-party platform (such as Google or Privy), we will have access to certain information from that platform, such as your email address, in accordance with the authorization procedures determined by that platform.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">2. How We Use Information</h2>
              <p>
                We use your personal information in accordance with your instructions, including any applicable terms in the Terms of Use, and as required by applicable law. We may also use the personal information we collect for:
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.1 Providing Services and Features</h3>
              <p>
                We use personal information to provide, maintain, and improve the Interface, including to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Operate, maintain, customize, measure, and improve the Interface</li>
                <li>Create and update user accounts</li>
                <li>Process transactions</li>
                <li>Send confirmations, notices, updates, security alerts, and support messages</li>
                <li>Facilitate trading, deposits, and withdrawals</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.2 Safety and Security</h3>
              <p>
                It is in our legitimate business interest to use your personal information to maintain safety, security, and integrity, including to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Protect, investigate, and deter against fraudulent, unauthorized, or illegal activity</li>
                <li>Monitor and verify identity or service access</li>
                <li>Combat spam, malware, or security risks</li>
                <li>Perform internal operations necessary to provide the Interface</li>
                <li>Troubleshoot software bugs and operational problems</li>
                <li>Enforce our agreements and Terms of Use</li>
                <li>Comply with applicable security laws and regulations</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.3 Customer Support</h3>
              <p>
                We use your personal information to provide customer support, including to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Direct questions to the appropriate support person</li>
                <li>Investigate and address user concerns</li>
                <li>Monitor and improve customer support responses</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.4 Personalization</h3>
              <p>
                We may use your personal information to personalize your experience and use of the Interface and our communications.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.5 Research and Development</h3>
              <p>
                It is in our legitimate business interest to use your personal information for testing, research, analysis, and product development to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Improve your experience</li>
                <li>Improve and enhance safety and security</li>
                <li>Prevent use of the Interface for illegal or improper purposes</li>
                <li>Develop new features and products</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.6 Legal and Regulatory Compliance</h3>
              <p>
                It is in our legitimate interests to use personal information to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Enforce compliance with our Terms of Use and policies</li>
                <li>Verify your identity by comparing information against third-party databases and public records</li>
                <li>Investigate or address claims or disputes</li>
                <li>Respond to regulators, government entities, and official inquiries</li>
                <li>Comply with applicable laws and regulations</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.7 Direct Marketing</h3>
              <p>
                Except where consent is required, it is in our legitimate interest to use information to market the Interface to you. This may include sending communications about features, promotions, surveys, news, updates, and events. If you do not want marketing communications, please opt out by selecting "unsubscribe" in any marketing email or by <a href="/contact" className="text-primary hover:underline">contacting us</a>.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.8 Anonymization and Aggregation</h3>
              <p>
                It is in our legitimate interests to aggregate or anonymize personal information in a form that does not allow you to be personally identified and use the resulting information for statistical analysis, such as to better understand our customer base or for other purposes.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">3. When We Share and Disclose Information</h2>
              <p>We may share your information in the following circumstances:</p>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.1 To Comply with Legal Obligations</h3>
              <p>We may share your information:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>To cooperate with government investigations</li>
                <li>When compelled by subpoena, court order, or similar legal procedure</li>
                <li>When we believe disclosure is necessary to prevent harm to another person</li>
                <li>To report suspected illegal activity</li>
                <li>To investigate violations of our Terms of Use or other policies</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.2 With Service Providers or Other Third Parties</h3>
              <p>
                We may share your personal information with service providers or other third parties who help facilitate business and compliance operations, such as:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Hosting and infrastructure providers</li>
                <li>Database services</li>
                <li>Email notification services</li>
                <li>Error logging and monitoring</li>
                <li>Security monitoring</li>
                <li>Analytics services</li>
                <li>Marketing and technology services</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.3 Partners</h3>
              <p>
                We may share your personal information with business partners, for example marketing partners, subject to any consent requirements.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.4 Affiliates</h3>
              <p>
                We may share your personal information with our subsidiaries and affiliates, for purposes consistent with this Policy.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.5 During a Change to Our Business</h3>
              <p>
                If we engage in a merger, acquisition, bankruptcy, dissolution, reorganization, sale of assets, financing, public offering, acquisition of business, or similar transaction, some or all of your personal information may be shared or transferred, subject to standard confidentiality arrangements.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.6 Aggregated or De-identified Data</h3>
              <p>
                We may share aggregated or anonymized data with other persons for their own uses.
              </p>

              <p className="mt-6 font-semibold">
                We do not sell your personal information for monetary value and will not sell it without providing a right to opt out.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">4. International Data Transfers</h2>
              <p>
                We operate globally, and we may need to transfer your personal data internationally. To provide secure and stable Interface, we may maintain servers in various locations. Your personal data may be processed outside your country of residence.
              </p>
              <p className="mt-4">
                Data protection laws vary from country to country, with some countries offering more protection than others. Wherever your personal data is processed, we apply the same protections as described in this Policy. We transfer your personal data in accordance with legal frameworks required in different jurisdictions.
              </p>
              <p className="mt-4">
                Recipients of your personal data must agree to at least the same level of privacy safeguards required by applicable data protection laws, such as:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>European Commission adequacy decisions</li>
                <li>UK adequacy regulations</li>
                <li>Swiss adequacy decisions</li>
                <li>EEA Standard Contractual Clauses (SCCs)</li>
                <li>UK Standard clause contracts</li>
              </ul>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">5. Data Retention</h2>
              <p>
                We retain your personal information only for as long as is necessary to fulfill the purposes for which it was collected and processed, in accordance with our retention procedures, and in accordance with applicable laws or until you withdraw your consent (where applicable).
              </p>
              <p className="mt-4">
                To determine the appropriate retention period, we consider:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>The amount, nature, and sensitivity of the personal information</li>
                <li>The potential risk of harm from unauthorized use or disclosure</li>
                <li>The purposes for which we use your personal information</li>
                <li>Whether we can achieve those purposes through other means</li>
                <li>Applicable legal requirements</li>
              </ul>
              <p className="mt-4">
                Trading and financial ledger data may be retained longer for compliance with legal obligations, accounting requirements, or to resolve disputes.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">6. Your Rights and Choices</h2>
              <p>
                To view or update your information, <a href="/contact" className="text-primary hover:underline">contact us</a>.
              </p>
              <p className="mt-4">
                If provided under any applicable law and subject to any limitations in such law, you may:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Ask us for access to your personal information we hold</li>
                <li>Request corrections or deletions of your personal information</li>
                <li>Request to restrict the processing of your personal information, or object to that processing</li>
                <li>Withdraw your consent (where we are processing based on consent)</li>
                <li>Request receipt or transfer to another organization, in a machine-readable form, of personal information you have provided to us</li>
                <li>Complain to your local data protection authority of violation of your privacy rights</li>
              </ul>
              <p className="mt-4">
                Prior to any response to the exercise of such rights, we will require you to verify your identity. We may have valid legal reasons to refuse your request and will inform you if that is the case.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">7. Security</h2>
              <p>
                We maintain administrative, technical, and physical safeguards designed to protect the personal information we maintain against unauthorized access, use, disclosure, alteration, or destruction in line with applicable data protection and privacy laws. We require our third-party service providers to implement appropriate safeguards.
              </p>
              <p className="mt-4">
                Our security measures include:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Encryption in transit (TLS)</li>
                <li>Restricted-access systems</li>
                <li>Firewalls and intrusion detection</li>
                <li>Secure infrastructure</li>
                <li>Monitoring for malicious activity</li>
                <li>Access controls and authentication</li>
              </ul>
              <p className="mt-4 font-semibold">
                No system can be completely secure. Therefore, although we take steps to secure your information, we cannot guarantee that your information, searches, or other communications will always remain secure. You are responsible for all activity on the Interface relating to any of your blockchain addresses or cryptocurrency wallets.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">8. Age Limitations</h2>
              <p>
                To the extent prohibited by applicable law, we do not allow use of the Interface by anyone younger than 18 years old. If you learn that anyone younger than 18 has unlawfully provided us with personal data, please <a href="/contact" className="text-primary hover:underline">contact us</a> and we will take steps to delete such information, close any such accounts, and prevent the user from continuing to use the Interface.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">9. Third-Party Links and Services</h2>
              <p>
                The Interface may contain links to third-party websites, mobile applications, or other online assets that are not owned or controlled by us. This Policy does not apply to those websites. If you choose to use those websites, please check the legal and privacy statements posted on each website, mobile application, or online asset you access to understand their privacy practices.
              </p>
              <p className="mt-4">
                We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">10. Cookies and Online Tracking</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">10.1 What Are Cookies</h3>
              <p>
                Cookies are small text files of letters and numbers that are stored and accessed on your browser or the hard drive of your device. Like many companies online, we use services that use cookies and other tracking technology to collect information about your browsing activities, interactions with websites, and the device you are using.
              </p>
              <p className="mt-4">
                The use of these technologies improves your experience when you browse or use the Interface, allows us to enhance functionality, increase security, and measure use and effectiveness.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">10.2 Types of Tracking Technologies</h3>
              
              <p className="mt-4 font-semibold">Pixels:</p>
              <p className="mt-2">
                A pixel is a small amount of code on a web page or in an email notification that works in conjunction with cookies to identify users and record user behavior. We use pixels to learn whether you have interacted with certain web or email content.
              </p>

              <p className="mt-4 font-semibold">Local Storage:</p>
              <p className="mt-2">
                Local storage is an industry-standard technology that allows a website or application to store information locally on your computer or mobile device. We use local storage, including HTML5, to customize what we show you based on your content consumption and past interactions.
              </p>

              <p className="mt-4 font-semibold">Device Identifiers:</p>
              <p className="mt-2">
                Device identifiers are distinctive numbers associated with a smartphone or similar handheld device. We use mobile device identifiers for purposes set out in this Policy, for example to recognize your device when you return to the Interface.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">10.3 Types of Cookies We Use</h3>
              
              <p className="mt-4 font-semibold">Strictly Necessary Cookies:</p>
              <p className="mt-2">
                These cookies are necessary for the Interface to function and cannot be switched off. They enable security, prevent fraud, debug the website, and are usually only set in response to actions you make. You can set your browser to block these cookies, but then some parts of the Interface will not work.
              </p>

              <p className="mt-4 font-semibold">Analytical or Performance Cookies:</p>
              <p className="mt-2">
                These cookies allow us to count visits and traffic sources so we can measure and improve performance. They help us know which pages are most and least popular and see how visitors move around the Interface. All information these cookies collect is aggregated and anonymous.
              </p>

              <p className="mt-4 font-semibold">Functional Cookies:</p>
              <p className="mt-2">
                These are used to recognize you when you return to the Interface. This enables us to personalize content for you, greet you by name, and remember your account preferences.
              </p>

              <p className="mt-4 font-semibold">Marketing Cookies:</p>
              <p className="mt-2">
                These cookies are used by third-party advertisers or publishers to display personalized advertising. They do this by tracking your activity across websites.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">10.4 Your Cookie Choices</h3>
              <p>
                You have options to object, control, or limit how we, our partners, and other third parties use cookies:
              </p>

              <p className="mt-4 font-semibold">Blocking cookies in your browser:</p>
              <p className="mt-2">
                Most browsers let you remove or reject cookies. To do this, follow the instructions in your browser settings. Many browsers accept cookies by default until you change your settings. For more information, visit www.allaboutcookies.org.
              </p>

              <p className="mt-4 font-semibold">Blocking advertising ID use in mobile settings:</p>
              <p className="mt-2">
                Your mobile device settings may provide functionality to limit use of the advertising ID associated with your mobile device for interest-based advertising purposes.
              </p>

              <p className="mt-4 font-semibold">Using privacy plug-ins or browsers:</p>
              <p className="mt-2">
                You can block websites from setting cookies by using a browser with privacy features, like Brave, or installing browser plugins like Privacy Badger, Ghostery, or uBlock Origin, and configuring them to block third-party cookies or trackers.
              </p>

              <p className="mt-4 font-semibold">Platform opt-outs:</p>
              <p className="mt-2">
                Google: https://adssettings.google.com
              </p>

              <p className="mt-4 font-semibold">Advertising industry opt-out tools:</p>
              <p className="mt-2">
                Digital Advertising Alliance: http://optout.aboutads.info
              </p>

              <p className="mt-4 font-semibold text-destructive">
                Note: If you disable cookies, you may not be able to enjoy all features of the Interface.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">11. Notice to California Residents (CCPA)</h2>
              <p>
                We are required by the California Consumer Privacy Act of 2018 ("CCPA") to provide this notice to California residents.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">11.1 CCPA Scope</h3>
              <p>
                This CCPA Notice applies only to California residents whose interactions with us are limited to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Visiting our websites</li>
                <li>Signing up for email alerts</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">11.2 Privacy Practices</h3>
              <p>
                We do not "sell" personal information (as defined in the CCPA) for monetary value and will not sell it without providing a right to opt out. Like many companies, we may use third-party cookies for our advertising purposes. To learn how to opt out, review the "Cookies and Online Tracking" section above.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">11.3 Privacy Rights</h3>
              <p>The CCPA grants California residents the following rights:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><span className="font-semibold">Information:</span> Request information about how we have collected, used, and shared your personal information during the past 12 months</li>
                <li><span className="font-semibold">Access:</span> Request a copy of the personal information we maintain about you</li>
                <li><span className="font-semibold">Deletion:</span> Ask us to delete personal information we collected or maintain about you</li>
              </ul>
              <p className="mt-4">
                The CCPA limits these rights by, for example, prohibiting us from providing certain sensitive information in response to an access request and limiting circumstances in which we must comply with a deletion request.
              </p>
              <p className="mt-4">
                You are entitled to exercise these rights free from discrimination.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">11.4 How to Submit a Request</h3>
              <p>
                To request access to or deletion of personal information, <a href="/contact" className="text-primary hover:underline">contact us</a>.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">11.5 Identity Verification</h3>
              <p>
                The CCPA requires us to verify the identity of individuals submitting requests before providing a substantive response. We will ask you to verify your identity when you submit a request.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">11.6 Authorized Agents</h3>
              <p>
                California residents can empower an "authorized agent" to submit requests on their behalf. We will require the authorized agent to have written authorization confirming that authority.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">12. Additional Information For U.S. State Residents</h2>
              <p>
                Several U.S. states have enacted privacy laws that grant their residents certain rights. If you reside in one of these states (such as California, Colorado, Connecticut, Utah, or Virginia), this section applies to you.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">12.1 Categories of Personal Information</h3>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Identifiers (such as digital asset wallet address, public blockchain data, email address, username, avatar, images)</li>
                <li>Commercial information (such as transaction data, trading history)</li>
                <li>Internet or other electronic network activity information (such as IP address, usage data)</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">12.2 Categories of Recipients</h3>
              <p>
                Cloud service providers, analytics companies, fraud prevention and information security companies, email service providers, customer service providers, affiliated entities, and law enforcement.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">12.3 Use of Personal Information</h3>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Enable our Interface</li>
                <li>Provide personalized notifications and information</li>
                <li>Improve and develop our Interface</li>
                <li>Offers and other communications</li>
                <li>Other legal reasons</li>
              </ul>

              <p className="mt-4 font-semibold">
                We do not "sell" or "share" personal information or disclose personal information for "targeted advertising", as those terms are defined under state privacy laws.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">12.4 Nondiscrimination</h3>
              <p>
                You have the right not to be discriminated against for exercising any of your privacy rights.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">12.5 Appeals</h3>
              <p>
                If we deny your request, you may appeal our decision by <a href="/contact" className="text-primary hover:underline">contacting us</a>. If you have concerns about the result of an appeal, you may contact the attorney general in the state where you reside.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">13. Additional Information For Individuals in Europe</h2>
              <p>
                If you are located in Europe (including the European Economic Area, United Kingdom, or Switzerland), the following section applies to you.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">13.1 International Transfers</h3>
              <p>
                Our Interface is offered from various locations. We may store information we collect in multiple jurisdictions. If you access the Interface from outside those locations, you understand that we may transfer your information to other jurisdictions, which may provide fewer protections than your jurisdiction of residence.
              </p>
              <p className="mt-4">
                Whenever we make restricted international transfers of personal information, we take steps to ensure your personal information receives an adequate level of protection by putting in place appropriate safeguards, such as contractual clauses.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">13.2 Legal Basis for Processing</h3>
              <p>
                When we process your personal data, we do so in reliance on the following lawful bases:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><span className="font-semibold">Contract:</span> To perform our responsibilities under our contract with you (e.g., providing the products and functionality you requested)</li>
                <li><span className="font-semibold">Legitimate Interest:</span> When we have a legitimate interest in processing your personal data to operate our business or protect our interests (e.g., to provide, maintain, and improve our products and services, conduct data analytics, combat fraud, maintain security, and communicate with you)</li>
                <li><span className="font-semibold">Legal Obligation:</span> To comply with our legal obligations (e.g., to maintain a record of your consents and track those who have opted out of communications)</li>
                <li><span className="font-semibold">Consent:</span> When we have your consent to do so (e.g., when you opt in to receive marketing communications). When consent is the legal basis, you may withdraw such consent at any time</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">13.3 Data Subject Rights</h3>
              <p>
                In addition to the rights specified above, you may have the right to object to certain processing or request we restrict certain processing. To exercise any of these rights, please <a href="/contact" className="text-primary hover:underline">contact us</a>.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">13.4 Complaints</h3>
              <p>
                If you have a concern about our processing of personal data, we encourage you to contact us first. However, if we are not able to resolve your concern, you have the right to lodge a complaint with the Data Protection Authority where you reside:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>For individuals in the European Union: https://edpb.europa.eu/about-edpb/board/members_en</li>
                <li>For individuals in the UK: https://ico.org.uk/global/contact-us/</li>
                <li>For individuals in Switzerland: https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html</li>
              </ul>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">14. Additional Information For Individuals in Canada</h2>
              <p>
                If you are a Canadian resident, this section applies to you.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">14.1 Consent</h3>
              <p>
                By submitting personal information to Nordex, or our service providers and agents, you consent to the collection, use, disclosure, and transfer of your personal information in accordance with this Policy and as permitted or required by law.
              </p>
              <p className="mt-4">
                You may withdraw your consent at any time by <a href="/contact" className="text-primary hover:underline">contacting us</a>. If you withdraw consent (or decide not to provide certain personal information), you acknowledge that Nordex may not be able to continue to provide you with certain products, functionality, or information.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">14.2 Retention</h3>
              <p>
                We store personal information for as long as necessary to carry out the purposes for which we originally collected it and for other business purposes explained in this Policy.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">15. Changes to This Policy</h2>
              <p>
                This Policy is current as of the Effective Date set forth above. We may change this Policy from time to time and will notify you by revising the date at the top of this Policy. If we make material changes, we will provide you with additional notice (such as by adding a statement on the Interface or sending you a notification).
              </p>
              <p className="mt-4">
                We encourage you to review this Policy regularly to stay informed about our information practices and the choices available to you.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">16. Contact Us</h2>
              <p>
                If you have questions about our approach to privacy or would like to make a complaint, please contact us:
              </p>
              <div className="mt-4 bg-card border border-border rounded-lg p-4">
                <p className="font-semibold">Nordex / AltariGroup</p>
                <p className="mt-2">
                  Contact:{" "}
                  <a 
                    href="/contact" 
                    className="text-primary hover:underline"
                  >
                    Contact Us
                  </a>
                </p>
                <p className="mt-2">
                  Website:{" "}
                  <a 
                    href="https://nordex.trade" 
                    className="text-primary hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://nordex.trade
                  </a>
                </p>
              </div>
            </section>
          </div>

        </div>
    </SupportLayout>
  );
};
